import { ReactElement } from 'react';
import { VStack, Box, Tabs } from '@chakra-ui/react';
import { FeedbackForm } from 'components/common/FeedbackForm/FeedbackForm';
import { ArticleContentSections } from 'components/Article/ArticleContentSections';
import { useIsLiked } from 'hooks/utils/useIsLiked';
import { useIsRead } from 'hooks/utils/useIsRead';
import { TabBar } from 'components/Article/TabBar';
import { TopInformation } from 'components/Article/TopInformation';
import { getReadingTime } from 'utils/getReadingTime';
import { MetaSection } from 'components/Article/MetaSection';
import { AppetizerSection } from 'components/Article/AppetizerSection';
import { ArticleQuestionSections } from 'components/Article/ArticleQuestionSections';
import { LiteratureSection } from 'components/Article/LiteratureSection';
import { ContentType } from '@novaheal/types';
import { TabData } from 'types/tab';
import { EducationIcon, QuestionMarkIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import { Breakpoints } from 'types/breakpoints';
import { ArticleHeroDesktop } from 'components/Article/ArticleHeroDesktop';
import { useArticleApi } from 'hooks/api/backend/useArticleApi';
import { PageSpinner } from 'components/atoms/PageSpinner';
import { QueryErrorState } from 'components/common/QueryErrorState';

type Props = {
  id: string;
};

export const Article = ({ id }: Props): ReactElement | null => {
  const { data: article, isError, isLoading, error, refetch } = useArticleApi(id);
  const isArticleAvailable = !!article;
  const { isLiked, handleLikeChange } = useIsLiked(
    isArticleAvailable ? article.id : '',
    ContentType.ARTICLE
  );
  const { isRead, handleReadChange } = useIsRead(isArticleAvailable ? article.id : undefined);

  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);

  if (isLoading) {
    return <PageSpinner />;
  }

  if (isError) {
    return (
      <QueryErrorState
        error={error}
        onRetry={() => refetch()}
        showBack={false}
        showSupportLink={false}
      />
    );
  }

  if (!article) {
    return null;
  }

  const readingTime = getReadingTime(article.contentSections);

  const tabs: TabData[] = [
    {
      id: 0,
      icon: <NovaIcon icon={EducationIcon} size="m" aria-hidden />,
      tabName: 'Lernen',
    },
    {
      id: 1,
      icon: <NovaIcon icon={QuestionMarkIcon} size="m" aria-hidden />,
      tabName: 'Fragen',
    },
  ];

  return (
    <Box width="100%" mb="40px">
      <ArticleHeroDesktop
        coverImageUrl={article.coverImageUrl}
        title={article.title}
        readingTime={readingTime}
        ageGroups={article.ageGroups}
        relevantYears={article.relevantYears}
        tags={article.tags}
        handleLikeChange={() => handleLikeChange(article.title)}
        isLiked={isLiked}
        handleReadChange={() => handleReadChange(article.title)}
        isRead={isRead}
      />
      <VStack
        width="100%"
        bg="background.primary"
        align="left"
        mt={{ base: '-30px', md: '0' }}
        gap="0"
        borderTopRadius="30px"
        boxShadow={{ base: '0px 0px 15px rgba(0, 0, 0, 0.05)', md: 'none' }}
        position="relative"
      >
        {!isDesktop && (
          <TopInformation
            isLoading={isLoading}
            parentFolder={article.folder}
            category={article.category}
            title={article.title}
            readingTime={readingTime}
            relevantYears={article.relevantYears}
            tags={article.tags}
            ageGroups={article.ageGroups}
          />
        )}
        <Box bg="background.primary" width="100%" position="relative">
          {/*  -15 px because we have padding on modal body */}
          <TabBar position="sticky" top="-15px" tabs={tabs}>
            <Tabs.Content value="0" p="20px 0 0">
              <Box bg="background.primary">
                {article.meta && <MetaSection metaData={article.meta} />}
                <AppetizerSection appetizerData={article.appetizer} />
                <ArticleContentSections contentSections={article.contentSections} />
                <LiteratureSection literatureData={article.literature} />
              </Box>
            </Tabs.Content>
            <Tabs.Content value="1" p="0" pt="l">
              {article.questionSections && (
                <ArticleQuestionSections questionSections={article.questionSections} />
              )}
            </Tabs.Content>
          </TabBar>

          <Box m="40px" bg="background.primary">
            <FeedbackForm type={ContentType.ARTICLE} id={article.id} title={article.title} />
          </Box>
        </Box>
      </VStack>
    </Box>
  );
};
